import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setOnSessionExpired } from '../api/client.js';

const AuthContext = createContext(null);

/**
 * AuthProvider — bootstraps session state from /api/me, exposes login/logout
 * helpers, and wires the apiFetch session-expired handler to flip state +
 * call the parent `onSessionExpired` hook (used by the app shell to redirect).
 *
 * State shape: { status, user, organization, refresh, logout, setAuthenticated }
 *   status ∈ 'loading' | 'authenticated' | 'unauthenticated' | 'error'
 */
export function AuthProvider({ children, onSessionExpired, autoBootstrap = true }) {
  const [state, setState] = useState({
    status: autoBootstrap ? 'loading' : 'unauthenticated',
    user: null,
    organization: null,
    error: null,
  });

  const refresh = useCallback(async () => {
    try {
      const data = await api.me();
      if (data && data.user && data.organization) {
        setState({
          status: 'authenticated',
          user: data.user,
          organization: data.organization,
          error: null,
        });
      } else {
        setState({
          status: 'unauthenticated',
          user: null,
          organization: null,
          error: null,
        });
      }
    } catch (e) {
      if (e && e.status === 401) {
        setState({
          status: 'unauthenticated',
          user: null,
          organization: null,
          error: null,
        });
      } else {
        setState({
          status: 'error',
          user: null,
          organization: null,
          error: e,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!autoBootstrap) return;
    refresh();
  }, [autoBootstrap, refresh]);

  useEffect(() => {
    setOnSessionExpired(() => {
      setState({
        status: 'unauthenticated',
        user: null,
        organization: null,
        error: null,
      });
      if (onSessionExpired) {
        try { onSessionExpired(); } catch { /* ignore */ }
      }
    });
    return () => setOnSessionExpired(null);
  }, [onSessionExpired]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* swallow: we still clear client state */
    }
    setState({
      status: 'unauthenticated',
      user: null,
      organization: null,
      error: null,
    });
  }, []);

  const setAuthenticated = useCallback((user, organization) => {
    setState({
      status: 'authenticated',
      user,
      organization,
      error: null,
    });
  }, []);

  const value = {
    status: state.status,
    user: state.user,
    organization: state.organization,
    error: state.error,
    refresh,
    logout,
    setAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
